import { CircularProgress, Divider, Link, Typography, useMediaQuery } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Collapse from '@material-ui/core/Collapse';
import { createStyles, makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import ShoppingCartOutlinedIcon from '@material-ui/icons/ShoppingCartOutlined';
import React from 'react';
import { Link as RouterLink, Redirect, useHistory } from "react-router-dom";
import { Mixpanel } from '../mixpanel';
import { categoryMap } from 'common-ts/dist/models/CategoryList';
import { isValidIndonesianPhoneInput } from 'common-ts/dist/models/Phone';
import { resetAddressDraft } from '../redux/AddressRedux';
import { getCourierOptions, calculateCompleteCartInvoice, getDiscountedPriceAmountWithPromotionsAndShipment, getNumberOfItems, resetCartAddressAndPaymentState, submitOrderRequest, updateUserTrigger } from '../redux/CartRedux';
import { useAppDispatch, useAppSelector } from '../reduxhooks';
import CartContent from './CartContent';
import { FareastButton } from './OverlayComponentButton';
import PageCheckoutAddress from './PageCheckoutAddress';
import PageCheckoutAddressWithSignUp from './PageCheckoutAddressWithSignUp';
import PageCheckoutOrderRejected from './PageCheckoutOrderRejected';
import PageCheckoutPayment from './PageCheckoutPayment';
import PageCheckoutPaymentFailure from './PageCheckoutPaymentFailure';
import PageCheckoutPaymentSuccess from './PageCheckoutPaymentSuccess';
import { sendWebsiteEventMessage, sendWebsiteLivestreamMessage } from '../telegrambotevents';
import Countdown from 'react-countdown';
import PageCheckoutCourier from './PageCheckoutCourier';

const useStyles = makeStyles((theme: Theme) =>
createStyles({
  phoneErrorText: {
    color: '#2e2e2e',
    backgroundColor: 'lightgrey',
    padding: '10px',
    margin: '14px 20px 0px 20px',
  },
  fullHeight: {
    display: 'flex',
    justifyContent: 'space-between',
    [theme.breakpoints.down('sm')]: {
      paddingBottom: '100px',
    },
  },
  titleLogoContainer: {
    height: '42px',
    marginTop: '34px',
    marginBottom: '10px',
  },
  titleLogo: {
    height: '24px',
    display: 'block', // We use block so that the img is unique horizontally in the div
    marginLeft: '40px',
  },
  checkoutFormContainer: {
    height: 'auto',
    width: '100%',
  },
  sideCartList: {
    display: 'none',
    height: '100vh',
    background: '#fafafa',
    width: '480px',
    [theme.breakpoints.up('md')]: {
      display: 'block',
    },
  },
  sideCartListInner: {
    background: '#fafafa',
    width: '432px',
    padding: '24px',
  },
  cartMainSegment: {
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: 'calc(100% - 480px)',
    },
  },
  cartMobile: {
    width: 'calc(100%-48px)',
    padding: '16px 24px 26px 24px',
    backgroundColor: '#fafafa',
  },
  cartMobileAccordion: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '18px',
    backgroundColor: '#fafafa',
    '&:active': {
      backgroundColor: '#eaeaea',
    }
  },
  cartMobileAccordionIcon: {
    marginRight: '12px',
    color: '#222',
  },
  cartMobileAccordionText: {
    fontSize: '13px',
    fontWeight: 400,
    color: '#222',
  },
  cartMobileAccordionIconOpenClose: {
    marginLeft: '4px',
    color: '#222',
  },
  cartMobileAccordionPrice: {
    color: '#222',
    fontSize: '14px',
    fontFamily: 'Open Sans, sans-serif',
  },
  cartMobileText: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  cartMobileContainer: {
    width: '100%',
  },
  nextStepButtonContainer: {
    justifyContent: 'flex-end',
    display: 'flex',
    marginTop: '16px',
  },
  nextStepButton: {
    marginRight: '20px',
    marginLeft: 'auto',
  },
  countdownComponent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  countdownMainText: {
    fontSize: "20px",
    fontWeight: 500,
    paddingTop: "8px",
    paddingBottom: "10px",
  },
  countdownMainTextTimeout: {
    fontSize: "24px",
    fontWeight: 500,
    paddingTop: "8px",
    paddingBottom: "16px",
  },
  countdownNumberText: {
    fontSize: "22px",
    fontWeight: 300,
    letterSpacing: "1.2px",
    fontFamily: 'Open Sans, sans-serif',
    paddingBottom: "22px",
    color: "#343434",
  },
  countdownSubTextHighlight: {
    fontSize: "12px",
    fontWeight: 500,
    color: "#000000",
  },
  countdownSubText: {
    fontSize: "12px",
    fontWeight: 300,
    color: "#343434",
    paddingBottom: "42px",
    textAlign: "center",
  },
  countdownSubTextTimeout: {
    fontSize: "12px",
    fontWeight: 300,
    color: "#343434",
    paddingBottom: "4px",
    paddingTop: "4px",
    textAlign: "center",
  },
  countdownButtonText: {
    fontSize: "12px",
    letterSpacing: "0.7px",
    fontWeight: 500,
  },
}),
);

export default function PageCheckout() {
  const [checkoutStep, setCheckoutStep] = React.useState("address");
  const [cartSegmentOpen, setCartSegmentOpen] = React.useState<boolean>(false);
  const [expirePaymentTime, setExpirePaymentTime] = React.useState<Date>(new Date());
  const dispatch = useAppDispatch();
  const classes = useStyles();
  const cart = useAppSelector((state) => state.cart);
  const storeProfile = useAppSelector((state) => state.storeProfile.profile);
  const productsList = useAppSelector((state) => state.productsList);
  const isLoggedIn = useAppSelector((state) => state.account.authState === 'signedin');
  const userTriggerCheckout = useAppSelector((state) => state.cart.userTriggerCheckout);
  const currentAddress = useAppSelector(state => state.addressList.addressDraft);
  const accountPhone = useAppSelector(state => state.account.phoneNumber);
  // Signed-in buyers reuse a saved address; one saved without a phone (old
  // bundles allowed it) must be completed via UBAH before NEXT — the order
  // would otherwise be submitted with no reachable number.
  const addressPhoneOk = isValidIndonesianPhoneInput(currentAddress.phoneNumber || accountPhone);
  // submitOrderRequest refused to send an order without a valid phone:
  // bring the shopper back to the address step to fill it in.
  React.useEffect(() => {
    if (cart.phoneError && checkoutStep === 'courier') {
      setCheckoutStep('address');
      (window as any)?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [cart.phoneError]);
  const userBodyMeasurementsDict = useAppSelector(state => state.userMeasurementsList.userBodyMeasurementsDictionary);
  const userClothingMeasurementsDict = useAppSelector(state => state.userMeasurementsList.userClothingMeasurementsDictionary);
  const totalPayment = useAppSelector(state => getDiscountedPriceAmountWithPromotionsAndShipment(state.cart, state.productsList, state.userMeasurementsList.userBodyMeasurementsDictionary, state.userMeasurementsList.userClothingMeasurementsDictionary));

  const theme = useTheme();
  const isMdOrUp = useMediaQuery(theme.breakpoints.up("md"));
  const history = useHistory();

  if (userTriggerCheckout) {
    dispatch(updateUserTrigger(false));
    dispatch(resetCartAddressAndPaymentState());
    dispatch(resetAddressDraft());
  }

  // paymentStep influences this UI component's state, since this payment step
  // is something out of control of the UI / is not trigerred by the UI.
  // The UI component monitors and responds to the outside payment actions.
  // TODO Pull paymentStep to redux level.
  const paymentStep = cart.orderStatus;
  if ((paymentStep === "success" && checkoutStep !== "success")
    || (paymentStep === "paymentFailed" && checkoutStep !== "paymentFailed")) {
    setCheckoutStep(paymentStep);
  } else if (paymentStep === "waitingForPayment" && checkoutStep !== "payment") {
    setCheckoutStep("payment");
  } else if (paymentStep === "rejected" && checkoutStep !== "rejected") {
    setCheckoutStep("rejected");
  }

  const checkoutStage: {[key:string]: any} = {
    "address": {
      activeStep: 0,
    },
    "courier": {
      activeStep: 0,
    },
    "payment": {
      activeStep: 1,
    },
    "success": {
      activeStep: 2,
    },
    "paymentFailed": {
      activeStep: 2,
    }, 
    "rejected": {
      activeStep: 2,
    }
  }

  const activeStep = checkoutStage[checkoutStep].activeStep;

  // If there are no items in the cart, and the process is not yet done,
  // we redirect the user to "HOME".
  // A rejected order empties the cart itself, and setCheckoutStep("rejected")
  // above only lands on the next render — without the orderStatus exemption
  // this would redirect home before the rejection dialog ever shows.
  if ((activeStep !== 2 && cart.orderStatus !== 'rejected' && getNumberOfItems(cart) < 1)
    || (!isLoggedIn && activeStep > 0)) {
    return (
      <Redirect to={categoryMap["HOME"]} />
    )
  }

  let children: JSX.Element[] = [];
  switch(checkoutStep) {
    case "address":
      if (isLoggedIn === false) {
      children.push(
        <PageCheckoutAddressWithSignUp checkoutStep={checkoutStep} setCheckoutStep={setCheckoutStep} isDisabled={false} /> 
      );
      }
      else {
      children.push(
        <PageCheckoutAddress checkoutStep={checkoutStep} setCheckoutStep={setCheckoutStep} isDisabled={false} /> 
      );
      if (currentAddress.creationDateTime) {
        if (cart.phoneError || !addressPhoneOk) {
          children.push(
            <Typography key="phone-error" variant="body2" className={classes.phoneErrorText}>
              {cart.phoneError ?? 'Nomor HP penerima belum valid — tekan UBAH pada alamat untuk mengisi nomor HP.'}
            </Typography>
          );
        }
        children.push(
          <Box className={classes.nextStepButtonContainer}>
            <FareastButton onClick={() => {
              // Fetch courier options
              if (currentAddress?.destinationKiriminAjaObject?.kecamatan_id) {
                dispatch(getCourierOptions({
                  dest: currentAddress?.destinationKiriminAjaObject?.kecamatan_id,
                }));

                sendWebsiteLivestreamMessage("[CHECKOUT] Address entered, fetching courier options.");

                setCheckoutStep("courier");
                (window as any)?.scrollTo({top: 0, behavior: 'smooth'});
              }
            }}
              disabled={!currentAddress.destinationKiriminAjaObject?.kecamatan_id || !currentAddress.kecamatan || !addressPhoneOk} buttonMarginLeftRight="20px"
              buttonMarginTopBottom="0px">
              <b>NEXT</b>
            </FareastButton>
          </Box>
        )
      }
      }
      break;
    case "courier":
      children.push(
        <PageCheckoutCourier checkoutStep={checkoutStep} setCheckoutStep={setCheckoutStep} isDisabled={false}
        handleSubmit={() => {
          sendWebsiteLivestreamMessage("[CHECKOUT] Address entered and courier selected, continuing to payment.");
          dispatch(submitOrderRequest());

          sendWebsiteLivestreamMessage("[CHECKOUT] Payment for Rp " + totalPayment + " initiated.");

          const currentDateTime = new Date();
          currentDateTime.setTime(currentDateTime.getTime() + (60 * 60 * 1000));
          setExpirePaymentTime(currentDateTime);
        }} /> 
      );
      break;
    case "payment":
      children.push(
        <PageCheckoutPayment checkoutStep={checkoutStep} setCheckoutStep={setCheckoutStep} /> 
      );
      break;
    case "success":
      children.push(
        <PageCheckoutPaymentSuccess checkoutStep={checkoutStep} setCheckoutStep={setCheckoutStep} /> 
      );
      break;
    case "paymentFailed":
      children.push(
        <PageCheckoutPaymentFailure checkoutStep={checkoutStep} setCheckoutStep={setCheckoutStep} /> 
      );
      break;
    case "rejected":
      children.push(
        <PageCheckoutOrderRejected checkoutStep={checkoutStep} setCheckoutStep={setCheckoutStep} rejectionMessage={cart.orderData?.message ?? ''} />
      );
      break;
  }

  const countdownRenderer = ({ hours, minutes, seconds, completed }: any) => {
    if (completed) {
      // Render a completed state
      return (
      <Box className={classes.countdownComponent}>
        <Typography className={classes.countdownMainTextTimeout} >Order Telah Dibatalkan</Typography>
        <Typography className={classes.countdownSubTextTimeout} 
        >Stok <span className={classes.countdownSubTextHighlight}>telah kami kembalikan</span> ke sistem.
        <br/>Buat order baru untuk membeli produk.</Typography>
        <FareastButton
          aria-label="balik"
          onClick={() => {
            history.push("/shop");
          }}
          buttonMarginLeftRight='0px'
          buttonMarginTopBottom='20px'
          style={{
            marginBottom: "48px",
          }}
        >
          <Typography className={classes.countdownButtonText}>
            LIHAT SEMUA PRODUK
          </Typography>
        </FareastButton>
      </Box>);
    } else {
      // Render a countdown
      return (
      <Box className={classes.countdownComponent}>
        <Typography className={classes.countdownMainText} >Selesaikan Pembayaran</Typography>
        <span className={classes.countdownNumberText}>{String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}</span>
        <Typography className={classes.countdownSubText} 
        >Kami <span className={classes.countdownSubTextHighlight}>menjaga stok pesanan kamu</span>
        <br/>selama waktu pembayaran</Typography>
      </Box>)
    }
  };

  return (
    <Box className={classes.fullHeight}>
      <Box className={classes.cartMainSegment}>
        <Box justifyContent="center" className={classes.titleLogoContainer} >
          <Link component={RouterLink} to={categoryMap["HOME"]} onClick={() => {
            Mixpanel.track('navigateRoute', {
              'routePath': '/',
              'routeSource': 'checkOut',
            });
            Mixpanel.track('checkoutExit', {
              'routeSource': 'checkOut',
            });
          }}>
            {
              storeProfile?.config?.logoUrl ?
              <img className={classes.titleLogo} src={storeProfile.config.logoUrl} alt="store logo"/> :
              <Typography style={{fontSize: '19px', fontWeight: 700, letterSpacing: '1.5px', whiteSpace: 'nowrap', color: 'inherit'}}>
                {storeProfile?.name ?? ''}
              </Typography>
            }
          </Link>
        </Box>
        <Box className={classes.checkoutFormContainer}>
          {
            checkoutStep === "payment" ?
            <Countdown
              date={expirePaymentTime}
              renderer={countdownRenderer}
              onComplete={(timeDelta, completedOnStart) => {
              }}
            />:
            null
          }
          {
            isMdOrUp ?
            null :
            <Box className={classes.cartMobileContainer}>
              <Divider/>
              <Box className={classes.cartMobileAccordion} onClick={() => setCartSegmentOpen(!cartSegmentOpen)}>
                <Box className={classes.cartMobileText}>
                  <ShoppingCartOutlinedIcon className={classes.cartMobileAccordionIcon} />
                  <Typography className={classes.cartMobileAccordionText}>
                    {
                      cartSegmentOpen ?
                      "Tutup Detail Pembelian" :
                      "Buka Detail Pembelian"
                    }
                  </Typography>
                  {
                    cartSegmentOpen ?
                    <ExpandLess fontSize="small" className={classes.cartMobileAccordionIconOpenClose}/> :
                    <ExpandMore fontSize="small" className={classes.cartMobileAccordionIconOpenClose}/>
                  }
                </Box>
                <Box>
                  <Typography className={classes.cartMobileAccordionPrice}>
                    {"Rp " + getDiscountedPriceAmountWithPromotionsAndShipment(cart, productsList, userBodyMeasurementsDict, userClothingMeasurementsDict).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  </Typography>
                </Box>
              </Box>
              <Divider/>
              <Collapse in={cartSegmentOpen}>
                <Box className={classes.cartMobile}>
                  <CartContent cart={cart} productsList={productsList}
                    isModifiable={false} 
                    withPromoField={false}/>
                </Box> 
                <Divider/>
              </Collapse>
            </Box>
          }
          {children}
        </Box>
      </Box>
      <Box className={classes.sideCartList} >
        <Box className={classes.sideCartListInner}>
          <CartContent cart={cart} productsList={productsList} 
            isModifiable={false} 
            withPromoField={false} />
        </Box>
      </Box>
    </Box>
  );
}